
import { supabase } from "@/integrations/supabase/client";
import { Subscription, SubscriptionService } from "@/lib/data";

export async function getSubscriptions() {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      *,
      customer:customers(*)
    `);
  
  if (error) {
    console.error('Error fetching subscriptions:', error);
    throw error;
  }
  
  return data.map(subscription => ({
    subscription_id: subscription.subscription_id,
    subscription_number: subscription.subscription_number,
    customer_id: subscription.customer_id,
    customer: subscription.customer ? {
      id: subscription.customer.id,
      customer_name: subscription.customer.customer_name,
      customer_number: subscription.customer.customer_number,
      email: subscription.customer.email,
      phone: subscription.customer.phone,
      customer_type: subscription.customer.customer_type as "private" | "business",
      postal_street: subscription.customer.postal_street,
      postal_zip_code: subscription.customer.postal_zip_code,
      postal_city: subscription.customer.postal_city,
      created_at: subscription.customer.created_at,
      updated_at: subscription.customer.updated_at,
      first_name: subscription.customer.first_name,
      last_name: subscription.customer.last_name
    } : null,
    status: subscription.status as "registered" | "active" | "cancelled" | "terminated",
    services: [],
    registered_at: subscription.registered_at,
    activation_date: subscription.activation_date,
    termination_date: subscription.termination_date,
    cancellation_date: subscription.cancellation_date,
    next_bill_date: subscription.next_bill_date,
    last_bill_date: subscription.last_bill_date,
    installation_street: subscription.installation_street,
    installation_zip_code: subscription.installation_zip_code,
    installation_city: subscription.installation_city,
    housing_type: subscription.housing_type as "apartment" | "house" | "office",
    updated_at: subscription.updated_at,
    created_at: subscription.created_at,
    alarm_com_id: subscription.alarm_com_id,
    created_by: subscription.created_by,
    invoice_reference: subscription.invoice_reference,
    contact_name: subscription.contact_name,
    contact_phone: subscription.contact_phone,
    contact_email: subscription.contact_email,
    prom_avarn: subscription.prom_avarn
  }));
}

export async function getSubscriptionById(id: string, options: { minimal?: boolean } = {}): Promise<Subscription | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('subscription_id', id)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching subscription:', error);
    throw error;
  }

  // Get the customer information
  const { data: customerData, error: customerError } = await supabase
    .from('customers')
    .select('*')
    .eq('id', data.customer_id)
    .single();
  
  if (customerError && customerError.code !== 'PGRST116') {
    console.error('Error fetching customer:', customerError);
    throw customerError;
  }

  let services: SubscriptionService[] = [];
  
  // Only fetch subscription services if not minimal request
  if (!options.minimal) {
    const { data: servicesData, error: servicesError } = await supabase
      .from('subscription_services')
      .select(`
        *,
        service:services(*)
      `)
      .eq('subscription_id', id);
    
    if (servicesError) {
      console.error('Error fetching subscription services:', servicesError);
      throw servicesError;
    }
    
    services = servicesData.map(service => ({
      id: service.id,
      subscription_id: service.subscription_id,
      service_id: service.service_id,
      service: {
        service_id: service.service.service_id,
        service_name: service.service.service_name,
        default_price: service.service.default_price,
        description: service.service.description,
        parent_service_id: service.service.parent_service_id
      },
      status: service.status as "registered" | "active" | "cancelled" | "terminated",
      registration_date: service.registration_date,
      activation_date: service.activation_date,
      termination_date: service.termination_date,
      cancellation_date: service.cancellation_date,
      price: service.price,
      last_bill_date: service.last_bill_date,
      next_bill_date: service.next_bill_date
    }));
  }

  return {
    subscription_id: data.subscription_id,
    subscription_number: data.subscription_number,
    customer_id: data.customer_id,
    customer: customerData ? {
      id: customerData.id,
      customer_name: customerData.customer_name,
      customer_number: customerData.customer_number,
      email: customerData.email,
      phone: customerData.phone,
      customer_type: customerData.customer_type as "private" | "business",
      postal_street: customerData.postal_street,
      postal_zip_code: customerData.postal_zip_code,
      postal_city: customerData.postal_city,
      created_at: customerData.created_at,
      updated_at: customerData.updated_at,
      first_name: customerData.first_name,
      last_name: customerData.last_name
    } : null,
    status: data.status as "registered" | "active" | "cancelled" | "terminated",
    services: services,
    registered_at: data.registered_at,
    activation_date: data.activation_date,
    termination_date: data.termination_date,
    cancellation_date: data.cancellation_date,
    next_bill_date: data.next_bill_date,
    last_bill_date: data.last_bill_date,
    installation_street: data.installation_street,
    installation_zip_code: data.installation_zip_code,
    installation_city: data.installation_city,
    housing_type: data.housing_type as "apartment" | "house" | "office",
    updated_at: data.updated_at,
    created_at: data.created_at,
    alarm_com_id: data.alarm_com_id,
    created_by: data.created_by,
    invoice_reference: data.invoice_reference,
    contact_name: data.contact_name,
    contact_phone: data.contact_phone,
    contact_email: data.contact_email,
    prom_avarn: data.prom_avarn
  };
}
